<template>
  <div class="global-container">
    <div class="content">
    <!-- Header -->
    <div class="header">
      <button class="back-btn" @click="goBack">
        <span>←</span>
      </button>
      <div class="header-text">
        <h1 class="title">Add Expense</h1>
        <p class="subtitle">Record a new expense</p>
      </div>
    </div>

    <!-- Form Card -->
    <div class="form-card">
      <!-- Expense Details Section -->
      <div class="form-section">
        <h3 class="section-title">
          <span class="section-icon">&#x1F4CB;</span>
          Expense Details
        </h3>

        <div class="form-group">
          <label class="form-label">Description <span class="required">*</span></label>
          <input
            v-model="formData.description"
            type="text"
            placeholder="e.g., AWS Monthly Bill"
            class="form-input"
          />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Vendor <span class="required">*</span></label>
            <select v-model="formData.partyID" class="form-select">
              <option value="">Select vendor</option>
              <option
                v-for="vendor in vendors"
                :key="vendor.partyID || vendor.id"
                :value="vendor.partyID || vendor.id"
              >
                {{ vendor.name }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Amount ($) <span class="required">*</span></label>
            <input
              v-model.number="formData.amount"
              type="number"
              placeholder="0"
              min="0.01"
              step="0.01"
              class="form-input"
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Date <span class="required">*</span></label>
            <input
              v-model="formData.expenseDate"
              type="date"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <!-- empty for alignment -->
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Notes (optional)</label>
          <textarea
            v-model="formData.notes"
            placeholder="Any additional notes about this expense..."
            class="form-textarea"
          ></textarea>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <button class="cancel-btn" @click="goBack">Cancel</button>
      <button class="create-btn" @click="createExpense" :disabled="expenseLoading">
        {{ expenseLoading ? 'Creating...' : 'Create Expense' }}
      </button>
    </div>
  </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AddExpense',
  data() {
    return {
      formData: {
        description: '',
        partyID: '',
        amount: 0,
        expenseDate: new Date().toISOString().split('T')[0],
        notes: ''
      }
    }
  },
  computed: {
    ...mapGetters('vendor', ['vendors']),
    ...mapGetters('expense', { expenseLoading: 'loading' })
  },
  methods: {
    ...mapActions('vendor', ['fetchVendors']),
    ...mapActions('expense', { storeCreateExpense: 'createExpense' }),

    async createExpense() {
      console.log('Description: ', this.formData.description)
      console.log('Party: ', this.formData.partyID)
      console.log('Amount: ', this.formData.amount)
      console.log('Expense Date: ', this.formData.expenseDate)
      console.log('Form Data: ', this.formData)
      if (!this.formData.description || !this.formData.partyID || !this.formData.amount || !this.formData.expenseDate) {
        this.$store.dispatch('notification/showError', 'Please fill in all required fields')
        return
      }

      if (this.formData.amount <= 0) {
        this.$store.dispatch('notification/showError', 'Amount must be greater than 0')
        return
      }

      try {
        const payload = {
          description: this.formData.description,
          vendorPartyID: parseInt(this.formData.partyID),
          amount: parseFloat(this.formData.amount),
          expenseDate: new Date(this.formData.expenseDate).toISOString(),
          notes: this.formData.notes || null
        }
        await this.storeCreateExpense(payload)
        this.goBack()
      } catch (error) {
        console.error('Create expense failed:', error)
      }
    },
    goBack() {
      this.$router.push('/expenses')
    }
  },
  created() {
    this.fetchVendors()
  }
}
</script>

<style scoped>
.add-expense-container {
  width: 100%;
  background: #f8fafc;
  margin-left: 280px;
  padding: 48px 40px;
  min-height: 100vh;
}

/* Header */
.header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 40px;
}

.back-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.back-btn:hover {
  color: #1f2937;
}

.header-text {
  flex: 1;
}

.title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
}

.subtitle {
  font-size: 14px;
  color: #9ca3af;
  font-weight: 400;
}

/* Form Card */
.form-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 32px;
  max-width: 540px;
}

/* Form Section */
.form-section {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-icon {
  font-size: 18px;
}

.required {
  color: #ef4444;
}

/* Form Groups */
.form-group {
  margin-bottom: 24px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 8px;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  transition: border 0.2s ease;
  font-family: inherit;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-input::placeholder,
.form-textarea::placeholder {
  color: #d1d5db;
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  max-width: 540px;
}

.cancel-btn {
  background: white;
  color: #374151;
  border: 1px solid #e5e7eb;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.create-btn {
  background: #2563eb;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.create-btn:hover {
  background: #1d4ed8;
}

.create-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .add-expense-container {
    margin-left: 0;
    padding: 24px 16px;
  }

  .form-card {
    padding: 24px;
    max-width: 100%;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    max-width: 100%;
    flex-direction: column-reverse;
  }

  .cancel-btn,
  .create-btn {
    width: 100%;
  }
}
</style>
